import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FiLogOut, FiMenu, FiX, FiHome, FiUsers, FiUserCheck, FiDollarSign, FiCalendar, FiStar, FiImage, FiGlobe, FiPackage, FiHardDrive, FiSend, FiTrash2, FiChevronDown, FiFileText, FiSettings, FiClipboard, FiHelpCircle, FiLayers, FiAward, FiTag } from 'react-icons/fi';
import { HiCodeBracket, HiOutlineBuildingStorefront } from 'react-icons/hi2';
import { SECTION_NAV_ITEMS, getSectionLogoUrl, getWatchLogoUrl, getFoLogoUrl, findSectionNavItem, isSectionNavActive, getErpWebAssetUrl } from '../utils/erpWebAssets';
import GodQuickActionsBar from './GodQuickActionsBar';

const headerLogoClass = 'h-12 w-12 min-w-[3rem] object-contain rounded-sm';
const sidebarLogoClass = 'h-8 w-8 min-w-[2rem] object-contain rounded-sm';
const sectionLogoActiveClass = 'ring-2 ring-blue-500 ring-offset-1 rounded-sm';
// py-4 (2rem) + h-12 logos (3rem) — keep in sync with header inner row
const HEADER_OFFSET_CLASS = 'pt-20';
const HEADER_TOP_OFFSET_CLASS = 'mt-20';

const sectionMenuItemClass =
    'flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 whitespace-nowrap';

const GZ_WATCH_NAV_ITEMS = [
    { href: '/watch', name: 'Watch Dashboard', icon: FiHome },
    { href: '/watch/products', name: 'Watch Products', icon: HiOutlineBuildingStorefront },
    { href: '/watch/blog', name: 'Watch Blog', icon: FiFileText },
    { href: '/watch/customers', name: 'Watch Customers', icon: FiUsers },
    { href: '/watch/orders', name: 'Watch Orders', icon: FiDollarSign },
    { href: '/watch/config', name: 'Watch Web Config', icon: FiSettings },
    { href: '/watch/audit-log', name: 'Watch Audit Log', icon: FiClipboard },
    { href: '/watch/recent-deletes', name: 'Watch Recent Deletes', icon: FiTrash2 },
];

const GZ_FO_NAV_ITEMS = [
    { href: '/foweb', name: 'FO Dashboard', icon: FiHome },
    { href: '/foweb/brands', name: 'FO Brands', icon: FiAward },
    { href: '/foweb/categories', name: 'FO Categories', icon: FiTag },
    { href: '/foweb/products', name: 'FO Products', icon: HiOutlineBuildingStorefront },
    { href: '/foweb/customers', name: 'FO Customers', icon: FiUsers },
    { href: '/foweb/foportal', name: 'FO Portal', icon: FiUserCheck },
    { href: '/foweb/orders', name: 'FO Orders', icon: FiDollarSign },
    { href: '/foweb/audit-log', name: 'FO Audit Log', icon: FiClipboard },
    { href: '/foweb/recent-deletes', name: 'FO Recent Deletes', icon: FiTrash2 },
];

function SectionChildVisual({ child }) {
    if (child.logo) {
        return (
            <img
                src={getSectionLogoUrl(child)}
                alt=""
                className="h-6 w-6 min-w-[1.5rem] object-contain rounded-sm shrink-0"
            />
        );
    }
    return null;
}

function SectionChildLink({ child, onClick, className = sectionMenuItemClass }) {
    const body = (
        <>
            <SectionChildVisual child={child} />
            <span className={child.strikethrough ? 'line-through' : ''}>{child.name}</span>
        </>
    );
    if (child.external) {
        return (
            <a href={child.path} className={className} onClick={onClick}>
                {body}
            </a>
        );
    }
    return (
        <Link to={child.path} className={className} onClick={onClick}>
            {body}
        </Link>
    );
}

function SectionDropdownPanel({ items, onClick }) {
    return (
        <div className="min-w-[11rem] rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5">
            {items.map((child) => (
                <SectionChildLink key={child.path} child={child} onClick={onClick} />
            ))}
        </div>
    );
}

function SidebarCircleBadge({ label }) {
    const fontClass =
        label.length <= 3 ? 'text-[8px]' :
        label.length === 4 ? 'text-[7px]' :
        'text-[5px] leading-none tracking-tighter';

    return (
        <span
            className={`inline-flex items-center justify-center h-8 w-8 min-w-[2rem] rounded-full border-2 border-gray-600 text-gray-700 font-bold shrink-0 ${fontClass}`}
            aria-hidden="true"
        >
            {label}
        </span>
    );
}

const Layout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
    const [profilePhotoError, setProfilePhotoError] = useState(false);
    const [headerMenuOpen, setHeaderMenuOpen] = useState(null);
    const [sidebarMenuOpen, setSidebarMenuOpen] = useState(null);
    const [gzWatchMenuOpen, setGzWatchMenuOpen] = useState(false);
    const [gzFoMenuOpen, setGzFoMenuOpen] = useState(false);

    const profileDisplayName = user.first_name || user.username || '?';
    const profileInitial = profileDisplayName.charAt(0).toUpperCase();

    useEffect(() => {
        setProfilePhotoError(false);
    }, [user.photo_url]);

    useEffect(() => {
        setHeaderMenuOpen(null);
        setSidebarMenuOpen(null);
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile((wasMobile) => {
                if (wasMobile !== mobile) {
                    setIsSidebarOpen(!mobile);
                }
                return mobile;
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Determine which nav items to show based on current path (memoized)
    const navItems = useMemo(() => {
        // GOD navigation items
        const godNavItems = [
            { path: '/god/dashboard', name: 'Dashboard', icon: FiHome },
            { path: '/god/god-members', name: 'All God Members', icon: FiUsers },
            { path: '/god/sbfNeedReview', name: 'SBF Need Review', badge: 'SBF' },
            { path: '/god/expiring-members', name: 'Expiring Members', icon: FiCalendar },
            { path: '/god/god-groups', name: 'God Groups', badge: 'GROUP' },
            { path: '/god/autobot', name: 'AutoBot', icon: FiSend },
            { path: '/god/shops', name: 'Shops', icon: HiOutlineBuildingStorefront },
            { path: '/god/reviewPage', name: 'Reviews', icon: FiStar },
            { path: '/god/non-members', name: 'Non-Members', badge: 'Non' },
            { path: '/god/consumers', name: 'Consumers', badge: 'User' },
            { path: '/god/slgwebconfig', name: 'SLG Web Config', icon: FiGlobe },
            { path: '/god/payments', name: 'Payments', icon: FiDollarSign },
            { path: '/god/commands', name: 'Commands', icon: HiCodeBracket },
            { path: '/god/audit-log', name: 'Audit Log', icon: FiClipboard },
            { path: '/god/RecentDelete', name: 'Recent Deletes', icon: FiTrash2 },
        ];

        // SBF navigation items
        const sbfNavItems = [
            { path: '/sbf/dashboard', name: 'Dashboard', icon: FiHome },
            { path: '/sbf/sbf-members', name: 'All SBF Member', icon: FiUsers },
            { path: '/sbf/sbf-payment', name: 'SBF Payment', icon: FiDollarSign },
            { path: '/sbf/audit-log', name: 'Audit Log', icon: FiClipboard },
            { path: '/sbf/RecentDelete', name: 'Recent Deletes', icon: FiTrash2 },
        ];

        // UNBLOCK navigation items
        const unblockNavItems = [
            { path: '/unblock/dashboard', name: 'Dashboard', icon: FiHome },
            { path: '/unblock/unblock-members', name: 'All Unblock Member', icon: FiUsers },
            { path: '/unblock/unblock-payments', name: 'Unblock Payment', icon: FiDollarSign },
            { path: '/unblock/web-config', name: 'Web Config', icon: FiGlobe },
            { path: '/unblock/tg-config', name: 'TG Config', icon: FiSend },
            { path: '/unblock/audit-log', name: 'Audit Log', icon: FiClipboard },
            { path: '/unblock/RecentDelete', name: 'Recent Deletes', icon: FiTrash2 },
        ];

        const redirectNavItems = [
            { path: '/redirect/dashboard', name: 'Dashboard', icon: FiHome },
            { path: '/redirect/groups', name: 'Groups', icon: FiLayers },
            { path: '/redirect/domains', name: 'Domains', icon: FiGlobe },
            { path: '/redirect/help', name: 'Help', icon: FiHelpCircle },
            { path: '/redirect/audit-log', name: 'Audit Log', icon: FiClipboard },
        ];

        // GZ navigation items (formerly FO)
        const foNavItems = [
            { path: '/gz/dashboard', name: 'GZ Dashboard', icon: FiHome },
            { path: '/gz/customer', name: 'GZ Customer', icon: FiUsers },
            { path: '/gz/products', name: 'GZ Products', icon: HiOutlineBuildingStorefront },
            { path: '/gz/orders', name: 'GZ Orders', icon: FiDollarSign },
            { path: '/gz/audit-log', name: 'GZ Audit Log', icon: FiClipboard },
            { path: '/gz/RecentDelete', name: 'GZ Recent Deletes', icon: FiTrash2 },
        ];

        // Simon navigation items
        const simonNavItems = [
            { path: '/simon/dashboard', name: 'Dashboard', icon: FiHome },
            { path: '/simon/receivable', name: 'Simon Receivable', icon: FiDollarSign },
            { path: '/simon/customers', name: 'Simon Customers', icon: FiUsers },
            { path: '/simon/products', name: 'Simon Products', icon: HiOutlineBuildingStorefront },
            { path: '/simon/bundle', name: 'Simon Bundles', icon: FiStar },
            { path: '/simon/order', name: 'Simon Orders', icon: FiCalendar },
            { path: '/simon/config', name: 'Simon Config', icon: FiGlobe },
            { path: '/simon/audit-log', name: 'Audit Log', icon: FiClipboard },
            { path: '/simon/RecentDelete', name: 'Recent Deletes', icon: FiTrash2 },
        ];

        // SFRT navigation items
        const sfrtNavItems = [
            { path: '/sfrt/dashboard', name: 'Dashboard', icon: FiHome },
            { path: '/sfrt/payment', name: 'SFRT Payment', icon: FiDollarSign },
            { path: '/sfrt/audit-log', name: 'Audit Log', icon: FiClipboard },
            { path: '/sfrt/RecentDelete', name: 'Recent Deletes', icon: FiTrash2 },
        ];

        // Ads navigation items
        const adsNavItems = [
            { path: '/ads/dashboard', name: 'Dashboard', icon: FiHome },
            { path: '/ads/list', name: 'All Ads', icon: FiImage },
            { path: '/ads/audit-log', name: 'Audit Log', icon: FiClipboard },
            { path: '/ads/RecentDelete', name: 'Recent Deletes', icon: FiTrash2 },
        ];

        // Superman navigation items
        const SupermanNavItems = [
            { path: '/superman/dashboard', name: 'Dashboard', icon: FiHome },
            { path: '/superman/members', name: 'Superman Members', icon: FiUsers },
            { path: '/superman/army', name: 'Superman Army', icon: FiUsers },
            { path: '/superman/payments', name: 'Superman Payments', icon: FiDollarSign },
            { path: '/superman/reviews', name: 'Reviews', icon: FiStar },
            { path: '/superman/audit-log', name: 'Audit Log', icon: FiClipboard },
            { path: '/superman/RecentDelete', name: 'Recent Deletes', icon: FiTrash2 },
        ];

        const sectionLogo = (matchPrefix) => {
            const item = findSectionNavItem(matchPrefix);
            return item?.logo ? getSectionLogoUrl(item) : undefined;
        };

        // Assets navigation items
        const assetsNavItems = [
            { path: '/assets/domains', name: 'Domains', icon: FiGlobe },
            { path: '/assets/servers', name: 'Servers', icon: FiHardDrive },
            { path: '/assets/telegram', name: 'Telegram', icon: FiSend },
            { path: '/assets/others', name: 'Others', icon: FiPackage },
            { path: '/assets/audit-log', name: 'Audit Log', icon: FiClipboard },
            { path: '/assets/RecentDelete', name: 'Recent Deletes', icon: FiTrash2 },
        ];

        // Analytics navigation items
        const analyticsNavItems = [
            { path: '/analytics', name: 'Analytics', icon: FiHome },
            { path: '/analytics/slgweb', name: 'SLGWeb Analytics', logo: sectionLogo('/god') },
            { path: '/analytics/reviewweb', name: 'ReviewWeb Analytics', logo: getErpWebAssetUrl('reviewweblogo.png') },
            { path: '/analytics/redirect', name: 'Redirect Analytics', logo: sectionLogo('/redirect') },
            { path: '/analytics/unblock', name: 'Unblock Analytics', logo: sectionLogo('/unblock') },
            { path: '/analytics/watch', name: 'Watch Analytics', logo: sectionLogo('/watch') },
            { path: '/analytics/simon', name: 'Simon Analytics', logo: sectionLogo('/simon') },
            { path: '/analytics/ads', name: 'Ads Analytics', logo: sectionLogo('/ads') },
        ];

        // Main Dashboard sidebar: links to each section's dashboard only
        const mainDashboardNavItems = [
            { path: '/main-dashboard', name: 'Overview', icon: FiHome },
            { path: '/exchange-rates', name: 'Exchange Rates', icon: FiDollarSign },
            { path: '/main-dashboard/audit-log', name: 'Audit Log', icon: FiClipboard },
            { path: '/main-dashboard/recent-deletes', name: 'Recent Deletes', icon: FiTrash2 },
            ...SECTION_NAV_ITEMS.map((item) => ({
                path: item.path,
                name: item.name,
                logo: item.logo ? getSectionLogoUrl(item) : null,
                strikethrough: item.strikethrough,
                external: item.external,
                children: item.children,
                matchPrefix: item.matchPrefix,
            })),
        ];

        if (location.pathname.startsWith('/main-dashboard') || location.pathname.startsWith('/exchange-rates')) {
            return mainDashboardNavItems;
        }
        if (location.pathname.startsWith('/assets')) {
            return assetsNavItems;
        }
        if (location.pathname.startsWith('/analytics')) {
            return analyticsNavItems;
        }
        if (location.pathname.startsWith('/god')) {
            return godNavItems;
        }
        if (location.pathname.startsWith('/simon')) {
            return simonNavItems;
        }
        if (location.pathname.startsWith('/sbf')) {
            return sbfNavItems;
        }
        if (location.pathname.startsWith('/unblock')) {
            return unblockNavItems;
        }
        if (location.pathname.startsWith('/redirect')) {
            return redirectNavItems;
        }
        if (location.pathname.startsWith('/gz')) {
            return foNavItems;
        }
        if (location.pathname.startsWith('/sfrt')) {
            return sfrtNavItems;
        }
        if (location.pathname.startsWith('/ads')) {
            return adsNavItems;
        }
        if (location.pathname.startsWith('/superman')) {
            return SupermanNavItems;
        }
        return godNavItems; // default
    }, [location.pathname]);

    const isMainArea = location.pathname.startsWith('/main-dashboard') || location.pathname.startsWith('/exchange-rates');

    const showSidebar = isMainArea ||
                        location.pathname.startsWith('/assets') ||
                        location.pathname.startsWith('/analytics') ||
                        location.pathname.startsWith('/god') ||
                        location.pathname.startsWith('/simon') ||
                        location.pathname.startsWith('/sbf') || 
                        location.pathname.startsWith('/unblock') ||
                        location.pathname.startsWith('/redirect') ||
                        location.pathname.startsWith('/gz') ||
                        location.pathname.startsWith('/ads') ||
                        location.pathname.startsWith('/superman') ||
                        location.pathname.startsWith('/sfrt');

    const handleNavClick = () => {
        if (isMobile) {
            setIsSidebarOpen(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <header className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
                <div className="max-w-full px-4 sm:px-8 py-4 flex justify-between items-center">
                    <div className="flex items-center">
                        <button
                            onClick={toggleSidebar}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            aria-label="Toggle Menu"
                        >
                            {isSidebarOpen ? <FiX size={20} /> : <FiMenu size={20} />}
                        </button>
                        <div className="flex items-center ml-4 space-x-4">
                            <Link 
                                to="/main-dashboard"
                                className={`transition-colors ${
                                    isMainArea
                                        ? 'text-blue-600' 
                                        : 'text-gray-700 hover:text-blue-600'
                                }`}
                                title="Main Dashboard"
                            >
                                <FiHome size={20} className="font-bold" />
                            </Link>
                            {SECTION_NAV_ITEMS.map((item) => {
                                const active = isSectionNavActive(location.pathname, item);
                                const logo = (
                                        <img
                                            src={getSectionLogoUrl(item)}
                                            alt={item.name}
                                            title={item.name}
                                            className={`${headerLogoClass} ${active ? sectionLogoActiveClass : 'opacity-80 hover:opacity-100'}`}
                                        />
                                    );

                                return (
                                    <React.Fragment key={item.path}>
                                        <span className="text-gray-400 font-bold">|</span>
                                        {item.children ? (
                                            <div className="relative group">
                                                <button
                                                    type="button"
                                                    title={item.name}
                                                    aria-haspopup="true"
                                                    aria-expanded={isMobile ? headerMenuOpen === item.path : undefined}
                                                    className="flex items-center"
                                                    onClick={() => {
                                                        if (isMobile) {
                                                            setHeaderMenuOpen((open) => (open === item.path ? null : item.path));
                                                        }
                                                    }}
                                                >
                                                    {logo}
                                                </button>
                                                <div
                                                    className={`absolute left-0 top-full z-[60] pt-1 ${
                                                        isMobile
                                                            ? headerMenuOpen === item.path
                                                                ? 'block'
                                                                : 'hidden'
                                                            : 'hidden group-hover:block'
                                                    }`}
                                                >
                                                    <SectionDropdownPanel items={item.children} />
                                                </div>
                                            </div>
                                        ) : item.external ? (
                                            <a href={item.path} title={item.name}>
                                                {logo}
                                            </a>
                                        ) : (
                                            <Link to={item.path} title={item.name}>
                                                {logo}
                                            </Link>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="hidden sm:flex items-center">
                            {user.photo_url && !profilePhotoError ? (
                                <img
                                    src={user.photo_url}
                                    alt="Profile"
                                    referrerPolicy="no-referrer"
                                    onError={() => setProfilePhotoError(true)}
                                    className="w-8 h-8 rounded-full mr-2 object-cover bg-gray-100"
                                />
                            ) : (
                                <div
                                    className="w-8 h-8 rounded-full mr-2 bg-blue-600 text-white flex items-center justify-center text-sm font-semibold shrink-0"
                                    title={profileDisplayName}
                                >
                                    {profileInitial}
                                </div>
                            )}
                            <span className="text-gray-700">{profileDisplayName}</span>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                            title="Logout"
                        >
                            <FiLogOut size={20} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Overlay for mobile */}
            {showSidebar && isMobile && isSidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 z-30"
                    onClick={toggleSidebar}
                    aria-hidden="true"
                ></div>
            )}

            {/* Sidebar - Only show for /god, /sbf, /unblock routes */}
            {showSidebar && (
                <div className={`fixed top-0 left-0 h-full overflow-y-auto bg-white shadow-lg transition-all duration-300 ease-in-out z-40 ${HEADER_TOP_OFFSET_CLASS}
                    ${isMobile 
                        ? `w-64 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
                        : `${isSidebarOpen ? 'w-64' : 'w-20'} translate-x-0`}`
                }>
                    <nav className="mt-4">
                        {navItems.map((item) => {
                            const showLabels = isMobile || isSidebarOpen;
                            const isActive = item.children
                                ? isSectionNavActive(location.pathname, item)
                                : item.path === '/main-dashboard'
                                    ? location.pathname === '/main-dashboard'
                                    : location.pathname === item.path ||
                                      (item.path !== '/analytics' &&
                                          location.pathname.startsWith(`${item.path}/`));
                            const linkClass = `flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors w-full
                                ${isActive ? 'bg-gray-100' : ''}
                                ${!isMobile && !isSidebarOpen ? 'justify-center' : 'justify-start'}`;
                            const labelClass = `ml-3 transition-all duration-300 whitespace-nowrap
                                ${!isMobile && !isSidebarOpen ? 'opacity-0 w-0' : 'opacity-100'}
                                ${item.strikethrough ? 'line-through' : ''}`;
                            const iconOrLogo = item.logo ? (
                                <img
                                    src={item.logo}
                                    alt={item.name}
                                    className={sidebarLogoClass}
                                />
                            ) : item.badge ? (
                                <SidebarCircleBadge label={item.badge} />
                            ) : item.icon ? (
                                <item.icon size={20} className="min-w-[20px]" />
                            ) : (
                                <FiHome size={20} className="min-w-[20px]" />
                            );

                            if (item.children) {
                                const menuOpen = sidebarMenuOpen === item.path;
                                return (
                                    <div key={item.path} className={`relative ${!isMobile ? 'group' : ''}`}>
                                        <button
                                            type="button"
                                            className={linkClass}
                                            aria-haspopup="true"
                                            aria-expanded={isMobile ? menuOpen : undefined}
                                            onClick={() => {
                                                if (isMobile) {
                                                    setSidebarMenuOpen((open) => (open === item.path ? null : item.path));
                                                }
                                            }}
                                        >
                                            {iconOrLogo}
                                            <span className={labelClass}>{item.name}</span>
                                            {showLabels && (
                                                <FiChevronDown
                                                    size={16}
                                                    className={`ml-auto shrink-0 transition-transform ${
                                                        isMobile && menuOpen ? 'rotate-180' : ''
                                                    }`}
                                                />
                                            )}
                                        </button>
                                        {!isMobile && (
                                            <div
                                                className={`absolute z-50 ${
                                                    isSidebarOpen
                                                        ? 'left-4 right-4 top-full pt-1 hidden group-hover:block'
                                                        : 'left-full top-0 pl-1 hidden group-hover:block'
                                                }`}
                                            >
                                                <SectionDropdownPanel items={item.children} />
                                            </div>
                                        )}
                                        {isMobile && menuOpen && (
                                            <div className="bg-gray-50 pl-8">
                                                {item.children.map((child) => (
                                                    <SectionChildLink
                                                        key={child.path}
                                                        child={child}
                                                        onClick={handleNavClick}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            if (item.external) {
                                return (
                                    <a
                                        key={item.path}
                                        href={item.path}
                                        onClick={handleNavClick}
                                        className={linkClass}
                                    >
                                        {iconOrLogo}
                                        <span className={labelClass}>{item.name}</span>
                                    </a>
                                );
                            }

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={handleNavClick}
                                    className={linkClass}
                                >
                                    {iconOrLogo}
                                    <span className={labelClass}>{item.name}</span>
                                </Link>
                            );
                        })}
                    </nav>
                    {location.pathname.startsWith('/gz') && (
                        <>
                        <div className="mx-4 my-2 h-px shrink-0 bg-slate-200" aria-hidden="true" />
                        <div className="bg-yellow-100">
                            <button
                                type="button"
                                className={`flex w-full items-center px-4 py-3 text-slate-800 hover:bg-yellow-200/70 transition-colors
                                    ${!isMobile && !isSidebarOpen ? 'justify-center' : 'justify-start'}`}
                                aria-haspopup="true"
                                aria-expanded={gzWatchMenuOpen}
                                title="Watch"
                                onClick={() => setGzWatchMenuOpen((open) => !open)}
                            >
                                <img
                                    src={getWatchLogoUrl()}
                                    alt="Watch"
                                    className={sidebarLogoClass}
                                />
                                <span
                                    className={`ml-3 transition-all duration-300 whitespace-nowrap
                                        ${!isMobile && !isSidebarOpen ? 'opacity-0 w-0' : 'opacity-100'}`}
                                >
                                    Watch
                                </span>
                                {(isMobile || isSidebarOpen) && (
                                    <FiChevronDown
                                        size={16}
                                        className={`ml-auto shrink-0 transition-transform ${gzWatchMenuOpen ? 'rotate-180' : ''}`}
                                    />
                                )}
                            </button>
                            {gzWatchMenuOpen &&
                                GZ_WATCH_NAV_ITEMS.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <a
                                            key={item.href}
                                            href={item.href}
                                            onClick={handleNavClick}
                                            className={`flex items-center px-4 py-3 text-slate-800 hover:bg-yellow-200/70 transition-colors
                                                ${!isMobile && !isSidebarOpen ? 'justify-center' : 'justify-start pl-8'}`}
                                            title={item.name}
                                        >
                                            <Icon size={20} className="min-w-[20px]" />
                                            <span
                                                className={`ml-3 transition-all duration-300 whitespace-nowrap
                                                    ${!isMobile && !isSidebarOpen ? 'opacity-0 w-0' : 'opacity-100'}`}
                                            >
                                                {item.name}
                                            </span>
                                        </a>
                                    );
                                })}
                        </div>
                        <div className="bg-sky-100">
                            <button
                                type="button"
                                className={`flex w-full items-center px-4 py-3 text-slate-800 hover:bg-sky-200/70 transition-colors
                                    ${!isMobile && !isSidebarOpen ? 'justify-center' : 'justify-start'}`}
                                aria-haspopup="true"
                                aria-expanded={gzFoMenuOpen}
                                title="FO"
                                onClick={() => setGzFoMenuOpen((open) => !open)}
                            >
                                <img
                                    src={getFoLogoUrl()}
                                    alt="FO"
                                    className={sidebarLogoClass}
                                />
                                <span
                                    className={`ml-3 transition-all duration-300 whitespace-nowrap
                                        ${!isMobile && !isSidebarOpen ? 'opacity-0 w-0' : 'opacity-100'}`}
                                >
                                    FO
                                </span>
                                {(isMobile || isSidebarOpen) && (
                                    <FiChevronDown
                                        size={16}
                                        className={`ml-auto shrink-0 transition-transform ${gzFoMenuOpen ? 'rotate-180' : ''}`}
                                    />
                                )}
                            </button>
                            {gzFoMenuOpen &&
                                GZ_FO_NAV_ITEMS.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <a
                                            key={item.href}
                                            href={item.href}
                                            onClick={handleNavClick}
                                            className={`flex items-center px-4 py-3 text-slate-800 hover:bg-sky-200/70 transition-colors
                                                ${!isMobile && !isSidebarOpen ? 'justify-center' : 'justify-start pl-8'}`}
                                            title={item.name}
                                        >
                                            <Icon size={20} className="min-w-[20px]" />
                                            <span
                                                className={`ml-3 transition-all duration-300 whitespace-nowrap
                                                    ${!isMobile && !isSidebarOpen ? 'opacity-0 w-0' : 'opacity-100'}`}
                                            >
                                                {item.name}
                                            </span>
                                        </a>
                                    );
                                })}
                        </div>
                        </>
                    )}
                </div>
            )}

            {/* Main Content */}
            <div className={`transition-all duration-300 ${HEADER_OFFSET_CLASS}
                ${isMobile 
                    ? 'ml-0' 
                    : showSidebar 
                        ? `${isSidebarOpen ? 'ml-64' : 'ml-20'}`
                        : 'ml-0'}`}>
                {/* Second header: floating quick-action buttons for GOD section (alignment matches page h1) */}
                {location.pathname.startsWith('/god') && <GodQuickActionsBar />}
                <main className="w-full max-w-none px-4 sm:px-8 pt-4">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout; 